import React, { useState, useEffect } from 'react';
import { History, TrendingUp, TrendingDown, Calendar, Filter, Download, Eye } from 'lucide-react';
import { getData } from '../../../../global/Globalapi';
import { showAlert } from '../../../DynamicComp/Alert';

const BatchAuditViewer = ({ batchId, productId, batchCode }) => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('batch'); // 'batch' or 'product'
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch audit history
  const fetchAuditHistory = async () => {
    if (!batchId && !productId) return;

    try {
      setLoading(true);
      let response;

      if (viewMode === 'batch' && batchId) {
        response = await getData(`/batch-audit/batch/${batchId}`);
      } else if (viewMode === 'product' && productId) {
        response = await getData(`/batch-audit/product/${productId}`);
      }

      console.log('📊 Audit response:', response);

      if (response?.data?.data) {
        setAuditHistory(response.data.data);
      } else if (response?.data) {
        setAuditHistory(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('❌ Error fetching audit history:', error);
      showAlert({
        type: 'error',
        message: 'Failed to fetch audit history',
        title: 'Error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditHistory();
  }, [batchId, productId, viewMode]);

  // Calculate changes between entries
  const calculateChange = (current, previous) => {
    if (!previous) return null;

    const quantityChange = current.quantity_in_hand - previous.quantity_in_hand;
    const priceChange = parseFloat(current.price) - parseFloat(previous.price);

    return {
      quantityChange,
      priceChange,
      statusChange: current.is_active !== previous.is_active
    };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get change icon
  const getChangeIcon = (change) => {
    if (change > 0) {
      return <TrendingUp className="text-green-600" size={16} />;
    } else if (change < 0) {
      return <TrendingDown className="text-red-600" size={16} />;
    }
    return null;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Batch Code', 'Quantity', 'Price', 'Status'];
    const rows = auditHistory.map(entry => [
      formatDate(entry.batch_audit_create_date_time),
      entry.batch_code,
      entry.quantity_in_hand,
      entry.price,
      entry.is_active ? 'Active' : 'Inactive'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-audit-${batchCode || 'history'}-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <History className="text-blue-600" size={28} />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Audit History</h2>
            {batchCode && (
              <p className="text-sm text-gray-600">Batch: {batchCode}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            <Filter size={18} />
            Filters
          </button>
          <button
            onClick={exportToCSV}
            disabled={auditHistory.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      {productId && (
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setViewMode('batch')}
            className={`px-4 py-2 rounded-lg ${
              viewMode === 'batch'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Single Batch
          </button>
          <button
            onClick={() => setViewMode('product')}
            className={`px-4 py-2 rounded-lg ${
              viewMode === 'product'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            All Product Batches
          </button>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          <button
            onClick={fetchAuditHistory}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Apply Filters
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Total Changes</p>
          <p className="text-2xl font-bold text-blue-600">{auditHistory.length}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Stock Increases</p>
          <p className="text-2xl font-bold text-green-600">
            {auditHistory.filter((entry, idx) => {
              if (idx === auditHistory.length - 1) return false;
              return entry.quantity_in_hand > auditHistory[idx + 1].quantity_in_hand;
            }).length}
          </p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600">Stock Decreases</p>
          <p className="text-2xl font-bold text-red-600">
            {auditHistory.filter((entry, idx) => {
              if (idx === auditHistory.length - 1) return false;
              return entry.quantity_in_hand < auditHistory[idx + 1].quantity_in_hand;
            }).length}
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading audit history...</p>
          </div>
        ) : auditHistory.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <History size={48} className="mx-auto mb-4 text-gray-400" />
            <p className="text-lg">No audit history found</p>
          </div>
        ) : (
          auditHistory.map((entry, index) => {
            const previousEntry = index < auditHistory.length - 1 ? auditHistory[index + 1] : null;
            const changes = calculateChange(entry, previousEntry);

            return (
              <div
                key={entry.batch_audit_id}
                className="flex gap-4 border-l-4 border-blue-500 pl-4 pb-4"
              >
                <div className="flex-shrink-0 w-32">
                  <div className="text-xs text-gray-500">
                    {formatDate(entry.batch_audit_create_date_time)}
                  </div>
                </div>

                <div className="flex-1 bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-mono text-sm text-blue-600 mb-1">
                        {entry.batch_code}
                      </p>
                      {entry.product_name && (
                        <p className="text-sm text-gray-600">{entry.product_name}</p>
                      )}
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        entry.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {entry.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Quantity</p>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">
                          {entry.quantity_in_hand}
                        </p>
                        {changes && changes.quantityChange !== 0 && (
                          <div className="flex items-center gap-1">
                            {getChangeIcon(changes.quantityChange)}
                            <span
                              className={`text-xs ${
                                changes.quantityChange > 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {Math.abs(changes.quantityChange)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-600">Price</p>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">
                          ₹{parseFloat(entry.price).toFixed(2)}
                        </p>
                        {changes && changes.priceChange !== 0 && (
                          <div className="flex items-center gap-1">
                            {getChangeIcon(changes.priceChange)}
                            <span
                              className={`text-xs ${
                                changes.priceChange > 0
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              ₹{Math.abs(changes.priceChange).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-600">Initial Qty</p>
                      <p className="font-semibold text-gray-800">
                        {entry.initial_quantity}
                      </p>
                    </div>

                    <div>
                      <p className="text-gray-600">Updated</p>
                      <p className="font-semibold text-gray-800">
                        {formatDate(entry.update_date_time)}
                      </p>
                    </div>
                  </div>

                  {changes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        {changes.quantityChange > 0 && (
                          <span className="text-green-600">
                            ✓ Stock increased by {changes.quantityChange} units
                          </span>
                        )}
                        {changes.quantityChange < 0 && (
                          <span className="text-red-600">
                            ↓ Stock decreased by {Math.abs(changes.quantityChange)} units
                          </span>
                        )}
                        {changes.priceChange !== 0 && (
                          <span className="ml-4">
                            Price {changes.priceChange > 0 ? 'increased' : 'decreased'} by ₹
                            {Math.abs(changes.priceChange).toFixed(2)}
                          </span>
                        )}
                        {changes.statusChange && (
                          <span className="ml-4 text-orange-600">
                            ⚠ Status changed
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BatchAuditViewer;